import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { 
    avalancheMISConfig, 
    transformAvalancheMISData,
    transformStatusDataForExcel
} from './excelUtilsAvalanche';

const getTimestamp = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 330); 
    const istDate = now.toISOString().split("T")[0];
    const istTime = now.toISOString().split("T")[1].split(".")[0].replace(/:/g, "");
    return `${istDate}_${istTime}`;
};

// STRICT DD-MM-YYYY FORMATTER FOR METADATA HEADER BOXES
const formatHeaderDate = (dateVal) => {
    if (!dateVal) return "NA";
    if (typeof dateVal === "string") {
        const parts = dateVal.split('-');
        if (parts.length === 3) {
            if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
            if (parts[2].length === 4) return `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
        return dateVal.replace(/\//g, "-"); 
    }
    return dateVal;
};

// ✅ ADDED: Red/Green/Yellow Conditional Formatting for Status Report
const applyStatusFormatting = (worksheet, columns) => {
    let subIndex = -1, notSubIndex = -1;
    columns.forEach((col, i) => {
        if (col.label === 'Report Submitted') subIndex = i + 1;
        if (col.label === 'Report Not Submitted') notSubIndex = i + 1;
    });
  
    if (subIndex === -1 || notSubIndex === -1) return;
  
    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const submitted = row.getCell(subIndex).value;
        const notSubmitted = row.getCell(notSubIndex).value;
  
        let color = 'FFFFFF00'; // Yellow
        if (submitted == 0) color = 'FFFF0000'; // Red
        else if (notSubmitted == 0) color = 'FF00FF00'; // Green
  
        row.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        });
    });
};

export const generateAvalancheExcel = async (config) => {
    const { data, selectedView, filename, toolName, columnOrder } = config;
    
// ----------------------------------------------------
    // 1. STATUS REPORT
    // ----------------------------------------------------
    if (toolName === 'avalanche_status' || toolName === 'avalanche_daily_report_status') {
        const rowsToParse = data.data || data; 
        const { columns, transformedData } = transformStatusDataForExcel(rowsToParse, columnOrder);
        
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet 1');
        
        // Add Headers & Make Bold
        const headerRow = worksheet.addRow(columns.map(c => c.label));
        headerRow.font = { bold: true };

        // ✅ FIX: Adjust column widths ("boxes") so full text is visible without getting cut off
        columns.forEach((col, i) => {
            const column = worksheet.getColumn(i + 1);
            // Make column width dynamically fit the header text length + extra padding
            column.width = Math.max(col.label.length + 5, 20); 
            column.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Insert Data
        transformedData.forEach(r => worksheet.addRow(columns.map(c => r[c.label])));

        // Apply Colors
        applyStatusFormatting(worksheet, columns);

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `DailyAvalancheReportStatus_${getTimestamp()}.xlsx`);
        return;
    }

    // 2. MIS REPORT
    const viewConfig = avalancheMISConfig[selectedView];
    if (!viewConfig) return alert('Invalid View');

    const { headers, rows } = transformAvalancheMISData(data, selectedView);

    const fromDate = formatHeaderDate(data.from_date);
    const toDate = formatHeaderDate(data.to_date);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    // Add Metadata Rows
    worksheet.addRow([`Report Name: ${viewConfig.title}`]);
    worksheet.addRow([`From Date: ${fromDate}`]);
    worksheet.addRow([`To Date: ${toDate}`]);

    // Force merge Cells strictly across the entire matrix (72+ columns)
    const totalColumns = headers.length > 0 ? headers.length : 72; 
    
    worksheet.mergeCells(1, 1, 1, totalColumns); 
    worksheet.mergeCells(2, 1, 2, totalColumns); 
    worksheet.mergeCells(3, 1, 3, totalColumns); 

    // Style the metadata header bold and left-aligned
    for(let i=1; i<=3; i++) {
        const cell = worksheet.getCell(i, 1);
        cell.font = { bold: true, size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
    }

    // Apply the Headers and Bold them
    worksheet.addRow(headers);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true }; 

    // Insert Data
    rows.forEach(r => worksheet.addRow(r));

    const buffer = await workbook.xlsx.writeBuffer();
    const exportFilename = `MIS_${viewConfig.title.replace(/_AVALANCHE_REPORT/g, '')}_Avalanche_Report_${getTimestamp()}.xlsx`;
    saveAs(new Blob([buffer]), exportFilename);
};